
// Типы для подписок

// Статусы подписки
export type SubscriptionStatus = 
  | 'active'           // Активная подписка
  | 'paused'           // Приостановлена
  | 'cancelled'        // Отменена
  | 'payment_update_needed'; // Требует обновления платежного метода

// Периодичность подписки
export type SubscriptionPeriod = 
  | 'daily'    // Ежедневно 
  | 'weekly'   // Еженедельно
  | 'monthly'  // Ежемесячно
  | 'yearly';  // Ежегодно

// Интервалы для совместимости с базой данных
export type SubscriptionInterval = 'day' | 'week' | 'month' | 'year';

// Базовый интерфейс для подписки (соответствует структуре в базе данных)
export interface SubscriptionBase {
  id: string;
  rental_id?: string;
  payment_method_id?: string | null;
  status: SubscriptionStatus | string;
  amount: number;
  interval: SubscriptionInterval;
  next_payment_date?: string | null;
  start_date: string;
  created_at?: string;
  updated_at?: string;
  card_last4?: string | null;
  card_first6?: string | null;
  card_expiry_month?: string | null;
  card_expiry_year?: string | null;
  card_type?: string | null;
}

// Интерфейс для объекта подписки в кamelCase (используется внутри приложения)
export interface Subscription {
  id: string;
  rentalId?: string;
  courierId?: string;
  status: SubscriptionStatus;
  previousStatus?: SubscriptionStatus | null;
  amount: number;
  period?: SubscriptionPeriod;
  interval?: SubscriptionInterval; // Для совместимости
  nextPaymentDate?: string;
  startDate?: string;
  createdAt?: string;
  updatedAt?: string;
  active?: boolean;
  cardLast4?: string | null;
  cardFirst6?: string | null;
  cardExpiryMonth?: string | null;
  cardExpiryYear?: string | null;
  cardType?: string | null;
  
  // Добавляем поля для совместимости (snake_case)
  rental_id?: string;
  payment_method_id?: string | null;
  start_date?: string;
  next_payment_date?: string | null;
  card_last4?: string | null;
  card_first6?: string | null;
  card_expiry_month?: string | null;
  card_expiry_year?: string | null;
  card_type?: string | null;
  
  // Связи с другими таблицами
  rentals?: any;
  bike?: any;
  courier?: any;
  payments?: any[];
  
  // Добавляем поля для отображения свойств связанных записей
  rentalHumanNumber?: string | null;
  courierName?: string | null;
  courierPhone?: string | null;
  bikeName?: string | null;
  tariffName?: string | null;
}

// Расширенный интерфейс с данными о курьере и аренде
export interface SubscriptionWithRelations extends Subscription {
  rentalHumanNumber?: string | null;
  courierName?: string | null;
  courierPhone?: string | null;
  bikeName?: string | null;
  tariffName?: string | null;
  rentals?: any;
  bike?: any;
  courier?: any;
  payments?: any[];
}

// Интерфейс для создания новой подписки
export interface SubscriptionCreate {
  rental_id: string;
  status?: SubscriptionStatus;
  amount: number;
  interval: SubscriptionInterval;
  start_date?: string;
  next_payment_date?: string;
  payment_method_id?: string | null;
  card_last4?: string | null;
  card_first6?: string | null;
  card_expiry_month?: string | null;
  card_expiry_year?: string | null;
  card_type?: string | null;
}

// Интерфейс для обновления подписки
export interface SubscriptionUpdate {
  id: string;
  status?: SubscriptionStatus;
  amount?: number;
  interval?: SubscriptionInterval;
  next_payment_date?: string;
  card_last4?: string | null;
  card_first6?: string | null;
  card_expiry_month?: string | null;
  card_expiry_year?: string | null;
  card_type?: string | null;
}

// Алиас для обратной совместимости
export type SubscriptionWithDetails = SubscriptionWithRelations;

// Функция для преобразования данных из базы в объект типа Subscription
export function mapSubscriptionFromDb(data: any): Subscription {
  return {
    // camelCase для клиентского кода
    id: data.id,
    rentalId: data.rental_id,
    status: data.status as SubscriptionStatus,
    amount: data.amount,
    interval: data.interval,
    period: mapIntervalToPeriod(data.interval),
    startDate: data.start_date,
    nextPaymentDate: data.next_payment_date,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    cardLast4: data.card_last4,
    cardFirst6: data.card_first6,
    cardExpiryMonth: data.card_expiry_month,
    cardExpiryYear: data.card_expiry_year,
    cardType: data.card_type,

    // Сохраняем оригинальные snake_case поля для API
    rental_id: data.rental_id,
    payment_method_id: data.payment_method_id,
    start_date: data.start_date,
    next_payment_date: data.next_payment_date,
    card_last4: data.card_last4,
    card_first6: data.card_first6,
    card_expiry_month: data.card_expiry_month,
    card_expiry_year: data.card_expiry_year,
    card_type: data.card_type,

    // Связи
    rentals: data.rentals,
    payments: data.payments,
    
    // Добавляем поля для отображения связанных данных
    rentalHumanNumber: data.rentals?.human_number || null,
    courierName: data.rentals?.courier?.name || null,
    courierPhone: data.rentals?.courier?.phone || null,
    bikeName: data.rentals?.bikes?.model || null
  };
}

// Преобразование interval в период
function mapIntervalToPeriod(interval?: SubscriptionInterval): SubscriptionPeriod | undefined {
  if (!interval) return undefined;
  
  switch (interval) {
    case 'day': return 'daily';
    case 'week': return 'weekly';
    case 'month': return 'monthly';
    case 'year': return 'yearly';
    default: return undefined;
  }
}

// Преобразование периода в interval
export function mapPeriodToInterval(period?: SubscriptionPeriod): SubscriptionInterval | undefined {
  if (!period) return undefined;
  
  switch (period) {
    case 'daily': return 'day';
    case 'weekly': return 'week';
    case 'monthly': return 'month';
    case 'yearly': return 'year';
    default: return undefined;
  }
}
